import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { cn } from "lib/utils";
import { Building2, User } from "lucide-react";
import queryString from "query-string";
import type { RootState } from "store/store";

import type { AddressFormData } from "@/types/address";

import { AddressFormSepomex } from "js/components/Address";
import { buildReportAddressPayload } from "js/components/ExpressReport/reportAddress";
import {
  CreditReport,
  Investigation,
  PFRequestData,
  PMRequestData,
} from "js/components/ExpressReport/types";
import { Button } from "js/components/ui/button";
import { Checkbox } from "js/components/ui/checkbox";
import { DatePicker } from "js/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";
import { Separator } from "js/components/ui/separator";

import { CreditReportException } from "js/exceptions/CreditReportException";

import { getCreditReport } from "../../../api/creditReport";
import { queryKeys } from "../../../hooks/queryKeys";
import {
  useStartPfCreditReport,
  useStartPmCreditReport,
} from "../../../hooks/useCreditReports";
import { safeLocalStorage } from "../../../utils/safeStorage";
import { toast } from "../../../utils/toast";
import { validateName } from "../../../utils/validators/expressReportValidators";
import {
  validateBirthDate,
  validateCURP,
  validateCheckbox,
  validateCompanyRFC,
  validatePhoneNotAdvisor,
  validatePhoneNumber,
  validateRFC,
  validateZipCode,
} from "../../../utils/validators/generalValidators";

interface CapturingInputsInterface {
  investigation: Investigation;
  creditReport: CreditReport;
  setCreditReport: (response: CreditReport) => void;
}

interface FormData {
  name: string;
  first_surname: string;
  second_surname: string;
  phone_number: string;
  dial_code: string;
  email: string;
  birth_date?: Date;
  rfc: string;
  curp: string;
  legal_entity_name?: string;
  street: string;
  exterior_number: string;
  interior_number: string;
  zip_code: string;
  neighborhood: string;
  city: string;
  municipality: string;
  state: string;
  accept_tyc: boolean;
  accept_electronic_media: boolean;
}

interface FormErrors {
  [key: string]: string | undefined;
}

export interface CapturingInputsRef {
  triggerSubmit: () => void;
}

const CapturingInputs = forwardRef<
  CapturingInputsRef,
  CapturingInputsInterface
>(({ investigation, creditReport, setCreditReport }, ref) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isNatural, setIsNatural] = useState<boolean>(true);

  const emailInputRef = useRef<EmailInputRef>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { userData } = useSelector((state: RootState) => state.entities.user);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: "",
    first_surname: "",
    second_surname: "",
    phone_number: "",
    dial_code: "+52",
    email: "",
    birth_date: undefined,
    rfc: "",
    curp: "",
    legal_entity_name: "",
    street: "",
    exterior_number: "",
    interior_number: "",
    zip_code: "",
    neighborhood: "",
    city: "",
    municipality: "",
    state: "",
    accept_tyc: false,
    accept_electronic_media: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  // Mutation hooks
  const startPfMutation = useStartPfCreditReport();
  const startPmMutation = useStartPmCreditReport();

  // Derive loading from mutations
  const isLoading = startPfMutation.isPending || startPmMutation.isPending;
  const isEmailLocked = Boolean(investigation.prospectus_email);

  const setUpperCase = (value: string) => {
    // remove accents and make it upper case
    return value
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(".", "");
  };

  const updateFormField = <K extends keyof FormData>(
    field: K,
    value: FormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBlur = async (field: keyof FormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = await validateField(field, formData[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const validateField = async (
    field: keyof FormData,
    value: any,
  ): Promise<string | undefined> => {
    let error: string | undefined;

    try {
      switch (field) {
        case "name":
        case "first_surname":
        case "second_surname":
          if (!value || value.trim() === "") {
            error = "Este campo es requerido";
          } else {
            await validateName(null, value);
          }
          break;

        case "birth_date":
          if (isNatural && !value) {
            error = "Este campo es requerido";
          } else if (value) {
            await validateBirthDate(null, value);
          }
          break;

        case "rfc":
          if (!value || value.trim() === "") {
            error = "Este campo es requerido";
          } else {
            if (isNatural) {
              await validateRFC(null, value);
            } else {
              await validateCompanyRFC(null, value);
            }
          }
          break;

        case "curp":
          if (!value || value.trim() === "") {
            error = "Este campo es requerido";
          } else {
            await validateCURP(null, value);
          }
          break;

        case "phone_number":
          if (!value || value.trim() === "") {
            error = "Este campo es requerido";
          } else {
            await validatePhoneNumber(null, value);
            await validatePhoneNotAdvisor(userData, value);
          }
          break;

        case "email":
          // Email validation is handled by EmailInput component
          break;

        case "legal_entity_name":
          if (!isNatural) {
            if (!value || value.trim() === "") {
              error = "Este campo es requerido";
            } else if (value.length < 2 || value.length > 75) {
              error = "Debe contener entre 2 y 75 caractéres";
            } else if (!/^[0-9a-zA-ZñÑ& ]+$/.test(value)) {
              // ^[0-9a-zA-ZñÑ& ]+$ — no +, ., ,, -, accents, parentheses, etc.
              error = "Solo se permiten letras, números, espacios y &";
            }
          }
          break;

        case "street":
          if (!value || value.trim() === "") {
            error = "Este campo es requerido";
          } else if (value.length < 2 || value.length > 80) {
            error = "Debe contener entre 2 y 80 caractéres";
          }
          break;

        case "exterior_number":
          if (!value || value.trim() === "") {
            error = "Este campo es requerido";
          } else if (value.length < 1 || value.length > 10) {
            error = "Debe contener entre 1 y 10 caractéres";
          }
          break;

        case "interior_number":
          if (value && value.length > 7) {
            error = "Debe contener máximo 7 caractéres";
          }
          break;

        case "zip_code":
          if (!value || value.trim() === "") {
            error = "Este campo es requerido";
          } else {
            await validateZipCode(null, value);
          }
          break;

        case "neighborhood":
        case "city":
          if (!value || value.trim() === "") {
            error = "Este campo es requerido";
          } else if (value.length < 2 || value.length > 65) {
            error = "Debe contener entre 2 y 65 caracteres";
          }
          break;

        case "municipality":
        case "state":
          if (!value || value.trim() === "") {
            error = "Este campo es requerido";
          }
          break;

        case "accept_tyc":
        case "accept_electronic_media":
          if (!value) {
            error = "Debes aceptar para continuar";
          } else {
            await validateCheckbox(null, value);
          }
          break;
      }
    } catch (err: unknown) {
      error = err instanceof Error ? err.message : "Valor inválido";
    }

    return error;
  };

  const validateAllFields = async (): Promise<boolean> => {
    const fieldsToValidate: (keyof FormData)[] = [
      "name",
      "first_surname",
      "second_surname",
      "phone_number",
      "rfc",
      "curp",
      "street",
      "exterior_number",
      "zip_code",
      "neighborhood",
      "city",
      "municipality",
      "state",
      "accept_tyc",
      "accept_electronic_media",
    ];

    if (isNatural) {
      fieldsToValidate.push("birth_date");
    } else {
      fieldsToValidate.push("legal_entity_name");
    }

    const newErrors: FormErrors = {};
    let hasErrors = false;

    if (!isEmailLocked) {
      const emailIsValid = await emailInputRef.current?.validate();
      if (!emailIsValid) {
        hasErrors = true;
      }
    }

    for (const field of fieldsToValidate) {
      const error = await validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    }

    // Buró de Crédito accepts two domicilio fields (direccion1+direccion2 in
    // PF, direccion+segundaDireccion in PM), each ≤ 40 chars. mb-sic-api
    // splits the concatenated address across both fields automatically, so
    // the form's effective limit is 80, not 40. We still reject anything
    // beyond 80 here because the backend's last-resort truncation would
    // lose user data silently.
    const concatenated = [
      formData.street,
      formData.exterior_number,
      formData.interior_number,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (concatenated.length > 80) {
      newErrors.street = `Calle + número exterior + interior excede 80 caracteres (actual: ${concatenated.length}). Acorta alguno de los campos.`;
      hasErrors = true;
    }

    setErrors(newErrors);
    setTouched(
      fieldsToValidate.reduce((acc, field) => ({ ...acc, [field]: true }), {}),
    );

    return !hasErrors;
  };

  // Handle address form changes
  const handleAddressChange = (addressData: AddressFormData) => {
    setFormData((prev) => ({
      ...prev,
      street: addressData.street,
      exterior_number: addressData.exterior_number,
      interior_number: addressData.interior_number,
      neighborhood: addressData.neighborhood,
      city: addressData.city,
      municipality: addressData.municipality,
      state: addressData.state,
      zip_code: addressData.zip_code,
    }));
  };

  // Get current address data from form
  const addressFormData: AddressFormData = {
    street: formData.street,
    exterior_number: formData.exterior_number,
    interior_number: formData.interior_number,
    neighborhood: formData.neighborhood,
    city: formData.city,
    municipality: formData.municipality,
    state: formData.state,
    zip_code: formData.zip_code,
  };

  // Get address-specific errors
  const addressErrors = {
    street: errors.street,
    exterior_number: errors.exterior_number,
    interior_number: errors.interior_number,
    neighborhood: errors.neighborhood,
    city: errors.city,
    municipality: errors.municipality,
    state: errors.state,
    zip_code: errors.zip_code,
  };

  // Get address-specific touched state
  const addressTouched = {
    street: touched.street,
    exterior_number: touched.exterior_number,
    interior_number: touched.interior_number,
    neighborhood: touched.neighborhood,
    city: touched.city,
    municipality: touched.municipality,
    state: touched.state,
    zip_code: touched.zip_code,
  };

  const onParseQueryParamsFromURL = () => {
    const parsedQueryParams = queryString.parse(location.search);
    const creditReportId = creditReport.id;
    const isGuarantor = !!parsedQueryParams.is_guarantor;

    if (creditReportId && isGuarantor) {
      safeLocalStorage.setItem(`is-guarantor-${creditReportId}`, "true");

      const currentURL = new URL(window.location.href);
      currentURL.search = "";

      navigate(currentURL.pathname, { replace: true });
    }
  };

  const onFinish = () => {
    const handleMutationSuccess = async (
      response: CreditReport | { code: string },
    ) => {
      setIsOpen(false);

      if (response && "code" in response) {
        const exception = new CreditReportException(response.code);
        toast.info(exception.message);

        try {
          const updatedCreditReport = await getCreditReport(creditReport.id);
          setCreditReport(updatedCreditReport);
        } catch {
          // If fetch fails, don't update state with invalid response
        }
        queryClient.invalidateQueries({
          queryKey: queryKeys.creditReports.detail(creditReport.id),
        });
        return;
      }

      setCreditReport(response);
    };

    if (isNatural) {
      const data: PFRequestData = {
        person: {
          name: formData.name,
          first_surname: formData.first_surname,
          second_surname: formData.second_surname,
          phone_number: formData.phone_number,
          dial_code: formData.dial_code,
          birth_date: formData.birth_date
            ? dayjs(formData.birth_date).format("DD-MM-YYYY")
            : "",
          rfc: formData.rfc,
          curp: formData.curp,
        },
        address: buildReportAddressPayload(formData),
      };
      if (data.address.interior_number == "") {
        delete data.address.interior_number;
      }
      startPfMutation.mutate(
        { reportId: creditReport.id, reportData: data },
        { onSuccess: handleMutationSuccess },
      );
      return;
    }

    const data: PMRequestData = {
      person: {
        name: formData.name,
        first_surname: formData.first_surname,
        second_surname: formData.second_surname,
        phone_number: formData.phone_number,
        dial_code: formData.dial_code,
        curp: formData.curp,
      },
      company_data: {
        legal_entity_name: formData.legal_entity_name || "",
        rfc: formData.rfc,
        address: buildReportAddressPayload(formData),
      },
    };

    if (data.company_data.address.interior_number == "") {
      delete data.company_data.address.interior_number;
    }

    startPmMutation.mutate(
      { reportId: creditReport.id, reportData: data },
      { onSuccess: handleMutationSuccess },
    );
  };

  useEffect(() => {
    onParseQueryParamsFromURL();
  }, []);

  useEffect(() => {
    if (investigation) {
      const newFormData: Partial<FormData> = {};

      if (investigation.prospectus_email) {
        newFormData.email = investigation.prospectus_email;
      }

      setIsNatural(investigation.legal_type === "natural");

      const { person } = investigation;

      if (person) {
        if (isNatural) {
          if (person.birth_date) {
            newFormData.birth_date = dayjs(
              person.birth_date,
              "YYYY-MM-DD",
            ).toDate();
          }
          newFormData.curp = person.curp || "";
        } else {
          newFormData.legal_entity_name = person.legal_entity_name || "";
        }

        newFormData.name = person.name || "";
        newFormData.first_surname = person.first_surname || "";
        newFormData.second_surname = person.second_surname || "";
        newFormData.phone_number = person.phone_number || "";
        newFormData.dial_code = person.dial_code || "+52";
        newFormData.rfc = person.rfc || "";
        newFormData.street = person.address?.street || "";
        newFormData.exterior_number = person.address?.exterior_number || "";
        newFormData.interior_number = person.address?.interior_number || "";
        newFormData.neighborhood = person.address?.neighborhood || "";
        newFormData.municipality = person.address?.municipality || "";
        newFormData.city = person.address?.city || "";
        newFormData.state = person.address?.state || "";
        newFormData.zip_code = person.address?.zip_code || "";
      }

      setFormData((prev) => ({ ...prev, ...newFormData }));
    }
  }, [creditReport, investigation]);

  const handleOpenModal = async () => {
    const isValid = await validateAllFields();
    if (isValid) {
      setIsOpen(true);
    } else {
      toast.error("Por favor corrige los errores en el formulario");
    }
  };

  // Expose submit function to parent component
  useImperativeHandle(ref, () => ({
    triggerSubmit: handleOpenModal,
  }));

  return (
    <div id="capturing-inputs" className="space-y-6">
      {/* Section 1: Datos del Solicitante / Representante Legal */}
      <div className="space-y-4">
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-gray-700" />
            <h3 className="text-base font-semibold text-gray-900">
              {isNatural
                ? "Datos del Solicitante"
                : "Datos del Representante legal"}
            </h3>
          </div>
          <p className="text-xs italic text-gray-500">
            Campos con asterisco (<span className="text-red-600">*</span>) son
            obligatorios.
          </p>
        </div>

        {/* Name fields */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Nombre */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-gray-700">
              Nombre(s) *
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) =>
                updateFormField("name", setUpperCase(e.target.value))
              }
              onBlur={() => handleBlur("name")}
              maxLength={26}
              disabled={isLoading}
              placeholder="Nombre(s)"
              className={cn(
                "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                touched.name && errors.name && "border-red-500",
              )}
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">{formData.name.length}/26</p>
              {touched.name && errors.name && (
                <p className="text-xs text-red-600">{errors.name}</p>
              )}
            </div>
          </div>

          {/* Apellido Paterno */}
          <div className="space-y-2">
            <Label
              htmlFor="first_surname"
              className="text-sm font-medium text-gray-700"
            >
              Apellido Paterno *
            </Label>
            <Input
              id="first_surname"
              type="text"
              value={formData.first_surname}
              onChange={(e) =>
                updateFormField("first_surname", setUpperCase(e.target.value))
              }
              onBlur={() => handleBlur("first_surname")}
              maxLength={26}
              disabled={isLoading}
              placeholder="Apellido Paterno"
              className={cn(
                "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                touched.first_surname &&
                  errors.first_surname &&
                  "border-red-500",
              )}
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {formData.first_surname.length}/26
              </p>
              {touched.first_surname && errors.first_surname && (
                <p className="text-xs text-red-600">{errors.first_surname}</p>
              )}
            </div>
          </div>

          {/* Apellido Materno */}
          <div className="space-y-2">
            <Label
              htmlFor="second_surname"
              className="text-sm font-medium text-gray-700"
            >
              Apellido Materno *
            </Label>
            <Input
              id="second_surname"
              type="text"
              value={formData.second_surname}
              onChange={(e) =>
                updateFormField("second_surname", setUpperCase(e.target.value))
              }
              onBlur={() => handleBlur("second_surname")}
              maxLength={26}
              disabled={isLoading}
              placeholder="Apellido Materno"
              className={cn(
                "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                touched.second_surname &&
                  errors.second_surname &&
                  "border-red-500",
              )}
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {formData.second_surname.length}/26
              </p>
              {touched.second_surname && errors.second_surname && (
                <p className="text-xs text-red-600">{errors.second_surname}</p>
              )}
            </div>
          </div>
        </div>

        {/* Natural Person Fields */}
        {isNatural ? (
          <>
            {/* Birth date, RFC, CURP */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Fecha de Nacimiento */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Fecha de Nacimiento *
                </Label>
                <DatePicker
                  value={formData.birth_date}
                  onChange={(date) => {
                    updateFormField("birth_date", date);
                    setTouched((prev) => ({ ...prev, birth_date: true }));
                  }}
                  disabled={isLoading}
                  placeholder="DD/MM/YYYY"
                  minDate={new Date("1900-01-01")}
                  maxDate={new Date()}
                  fromYear={1900}
                  toYear={new Date().getFullYear()}
                  defaultMonth={new Date(2000, 0)}
                  hasError={touched.birth_date && !!errors.birth_date}
                  formatDisplay={(date) =>
                    dayjs(date).locale("es").format("DD/MM/YYYY")
                  }
                />
                {touched.birth_date && errors.birth_date && (
                  <p className="text-xs text-red-600">{errors.birth_date}</p>
                )}
              </div>

              {/* RFC */}
              <div className="space-y-2">
                <Label
                  htmlFor="rfc"
                  className="text-sm font-medium text-gray-700"
                >
                  RFC *
                </Label>
                <Input
                  id="rfc"
                  type="text"
                  value={formData.rfc}
                  onChange={(e) =>
                    updateFormField("rfc", setUpperCase(e.target.value))
                  }
                  onBlur={() => handleBlur("rfc")}
                  maxLength={13}
                  disabled={isLoading}
                  placeholder="RFC"
                  className={cn(
                    "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                    touched.rfc && errors.rfc && "border-red-500",
                  )}
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {formData.rfc.length}/13
                  </p>
                  {touched.rfc && errors.rfc && (
                    <p className="text-xs text-red-600">{errors.rfc}</p>
                  )}
                </div>
              </div>

              {/* CURP */}
              <div className="space-y-2">
                <Label
                  htmlFor="curp"
                  className="text-sm font-medium text-gray-700"
                >
                  CURP *
                </Label>
                <Input
                  id="curp"
                  type="text"
                  value={formData.curp}
                  onChange={(e) =>
                    updateFormField("curp", setUpperCase(e.target.value))
                  }
                  onBlur={() => handleBlur("curp")}
                  maxLength={18}
                  disabled={isLoading}
                  placeholder="CURP"
                  className={cn(
                    "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                    touched.curp && errors.curp && "border-red-500",
                  )}
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {formData.curp.length}/18
                  </p>
                  {touched.curp && errors.curp && (
                    <p className="text-xs text-red-600">{errors.curp}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Phone and Email */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Teléfono */}
              <div className="space-y-2">
                <Label
                  htmlFor="phone_number"
                  className="text-sm font-medium text-gray-700"
                >
                  Teléfono *
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.dial_code}
                    onValueChange={(value) =>
                      updateFormField("dial_code", value)
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-24 border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+52">+52</SelectItem>
                      <SelectItem value="+1">+1</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="phone_number"
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) =>
                      updateFormField(
                        "phone_number",
                        setUpperCase(e.target.value),
                      )
                    }
                    onBlur={() => handleBlur("phone_number")}
                    maxLength={10}
                    disabled={isLoading}
                    placeholder="Teléfono"
                    className={cn(
                      "flex-1 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                      touched.phone_number &&
                        errors.phone_number &&
                        "border-red-500",
                    )}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {formData.phone_number.length}/10
                  </p>
                  {touched.phone_number && errors.phone_number && (
                    <p className="text-xs text-red-600">
                      {errors.phone_number}
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="md:col-span-2">
                <EmailInput
                  ref={emailInputRef}
                  value={formData.email}
                  onChange={(value) => updateFormField("email", value)}
                  label="Correo Electrónico"
                  placeholder="correo@ejemplo.com"
                  required
                  disabled={isEmailLocked || isLoading}
                  showIcon
                  showStatus={!isEmailLocked}
                  className="h-10"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Legal Entity Representative Fields */}
            {/* CURP, Phone, Email */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* CURP */}
              <div className="space-y-2">
                <Label
                  htmlFor="curp"
                  className="text-sm font-medium text-gray-700"
                >
                  CURP *
                </Label>
                <Input
                  id="curp"
                  type="text"
                  value={formData.curp}
                  onChange={(e) =>
                    updateFormField("curp", setUpperCase(e.target.value))
                  }
                  onBlur={() => handleBlur("curp")}
                  maxLength={18}
                  disabled={isLoading}
                  placeholder="CURP"
                  className={cn(
                    "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                    touched.curp && errors.curp && "border-red-500",
                  )}
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {formData.curp.length}/18
                  </p>
                  {touched.curp && errors.curp && (
                    <p className="text-xs text-red-600">{errors.curp}</p>
                  )}
                </div>
              </div>

              {/* Teléfono */}
              <div className="space-y-2">
                <Label
                  htmlFor="phone_number"
                  className="text-sm font-medium text-gray-700"
                >
                  Teléfono *
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.dial_code}
                    onValueChange={(value) =>
                      updateFormField("dial_code", value)
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-24 border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+52">+52</SelectItem>
                      <SelectItem value="+1">+1</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="phone_number"
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) =>
                      updateFormField(
                        "phone_number",
                        setUpperCase(e.target.value),
                      )
                    }
                    onBlur={() => handleBlur("phone_number")}
                    maxLength={10}
                    disabled={isLoading}
                    placeholder="Teléfono"
                    className={cn(
                      "flex-1 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                      touched.phone_number &&
                        errors.phone_number &&
                        "border-red-500",
                    )}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {formData.phone_number.length}/10
                  </p>
                  {touched.phone_number && errors.phone_number && (
                    <p className="text-xs text-red-600">
                      {errors.phone_number}
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <EmailInput
                  ref={emailInputRef}
                  value={formData.email}
                  onChange={(value) => updateFormField("email", value)}
                  label="Correo Electrónico"
                  placeholder="correo@ejemplo.com"
                  required
                  disabled={isEmailLocked || isLoading}
                  showIcon
                  showStatus={!isEmailLocked}
                  className="h-10"
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Section 2: Datos de la Empresa (only for legal entities) */}
      {!isNatural && (
        <>
          <Separator className="my-6" />
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2">
              <Building2 className="h-4 w-4 text-gray-700" />
              <h3 className="text-base font-semibold text-gray-900">
                Datos de la Empresa
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Razón Social */}
              <div className="space-y-2">
                <Label
                  htmlFor="legal_entity_name"
                  className="text-sm font-medium text-gray-700"
                >
                  Razón Social *
                </Label>
                <Input
                  id="legal_entity_name"
                  type="text"
                  value={formData.legal_entity_name}
                  onChange={(e) =>
                    updateFormField(
                      "legal_entity_name",
                      setUpperCase(e.target.value),
                    )
                  }
                  onBlur={() => handleBlur("legal_entity_name")}
                  maxLength={75}
                  disabled={isLoading}
                  placeholder="Razón Social"
                  className={cn(
                    "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                    touched.legal_entity_name &&
                      errors.legal_entity_name &&
                      "border-red-500",
                  )}
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {formData.legal_entity_name?.length || 0}/75
                  </p>
                  {touched.legal_entity_name && errors.legal_entity_name && (
                    <p className="text-xs text-red-600">
                      {errors.legal_entity_name}
                    </p>
                  )}
                </div>
              </div>

              {/* RFC */}
              <div className="space-y-2">
                <Label
                  htmlFor="rfc"
                  className="text-sm font-medium text-gray-700"
                >
                  RFC *
                </Label>
                <Input
                  id="rfc"
                  type="text"
                  value={formData.rfc}
                  onChange={(e) =>
                    updateFormField("rfc", setUpperCase(e.target.value))
                  }
                  onBlur={() => handleBlur("rfc")}
                  maxLength={12}
                  disabled={isLoading}
                  placeholder="RFC"
                  className={cn(
                    "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
                    touched.rfc && errors.rfc && "border-red-500",
                  )}
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {formData.rfc.length}/12
                  </p>
                  {touched.rfc && errors.rfc && (
                    <p className="text-xs text-red-600">{errors.rfc}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Section 3: Address Fields
          Uses the legacy SEPOMEX-powered form (CP → auto-fill ciudad /
          municipio / estado / colonia combobox) per client request for
          the "autorización de buró" page only. See AddressFormSepomex. */}
      <Separator className="my-6" />
      <AddressFormSepomex
        value={addressFormData}
        onChange={handleAddressChange}
        onBlur={(field) => handleBlur(field)}
        errors={addressErrors}
        touched={addressTouched}
        disabled={isLoading}
        isLoading={isLoading}
        title={isNatural ? "Domicilio" : "Domicilio de la Empresa"}
        transformValue={setUpperCase}
      />

      {/* Section 4: Terms and Conditions */}
      <Separator className="my-6" />
      <div className="space-y-4">
        {/* Accept Terms & Conditions */}
        <div className="flex items-start space-x-2">
          <Checkbox
            id="accept_tyc"
            checked={formData.accept_tyc}
            onCheckedChange={(checked) => {
              updateFormField("accept_tyc", checked === true);
              setTouched((prev) => ({ ...prev, accept_tyc: true }));
            }}
            disabled={isLoading}
            className="mt-0.5"
          />
          <Label
            htmlFor="accept_tyc"
            className="cursor-pointer text-sm leading-normal text-gray-700"
          >
            Acepto los{" "}
            <a
              href="https://www.multiburo.com.mx/terminos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              términos y condiciones
            </a>{" "}
            asi como el{" "}
            <a
              href="https://www.multiburo.com.mx/privacidad"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              aviso de privacidad
            </a>
            .
          </Label>
        </div>
        {touched.accept_tyc && errors.accept_tyc && (
          <p className="text-xs text-red-600">{errors.accept_tyc}</p>
        )}

        {/* Accept Electronic Media */}
        <div className="flex items-start space-x-2">
          <Checkbox
            id="accept_electronic_media"
            checked={formData.accept_electronic_media}
            onCheckedChange={(checked) => {
              updateFormField("accept_electronic_media", checked === true);
              setTouched((prev) => ({
                ...prev,
                accept_electronic_media: true,
              }));
            }}
            disabled={isLoading}
            className="mt-0.5"
          />
          <Label
            htmlFor="accept_electronic_media"
            className="cursor-pointer text-sm leading-normal text-gray-700"
          >
            Otorgo mi consentimiento expreso para acceder a los servicios
            ofrecidos por Multiburó SAPI de C.V mediante medios electrónicos
            tales como NIP.
          </Label>
        </div>
        {touched.accept_electronic_media && errors.accept_electronic_media && (
          <p className="text-xs text-red-600">
            {errors.accept_electronic_media}
          </p>
        )}
      </div>

      {/* Confirmation Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="sm:max-w-md"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Revisa y confirma tus datos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Asegúrate de que tu{" "}
              <span className="font-semibold">correo y número</span> sean
              correctos.
            </p>
            <div className="rounded-lg bg-gray-50 p-4 text-center">
              <p className="text-lg font-semibold text-gray-900">
                {formData.email}
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {formData.dial_code} {formData.phone_number}
              </p>
            </div>
            <p className="text-sm text-gray-600">
              Recuerda que ahí enviaremos tu{" "}
              <span className="font-semibold">NIP</span> de autorización.
            </p>
          </div>
          <DialogFooter className="flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </Button>
            <Button
              onClick={onFinish}
              disabled={isLoading}
              className="bg-[#0060ff] text-white hover:bg-[#0052d4]"
            >
              {isLoading ? "Confirmando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

CapturingInputs.displayName = "CapturingInputs";

export default CapturingInputs;
